import { tokens } from "@core/theme/tokens";
import { DialogHeader } from "./DialogHeader";
import { Modal, PortalButton } from "./PortalPrimitives";
import { portalPrimitiveValues as values } from "./portalPrimitiveValues";

// SPEC-201 §4.3: toda acción destructiva o de impacto amplio muestra el
// impacto, no solo pregunta. El cálculo del impacto (cuántos registros,
// cuántos usuarios) es de cada módulo — este componente solo lo presenta.
// PLAN-009 §3 (2026-09-21): la prop `error` que este diálogo exponía se
// retiró — un error de servicio u operación al confirmar va siempre al
// toast (`reportError`), nunca a un aviso dentro del diálogo. Sus tres
// consumidores (UsersScreen, OffersScreen, RuleEditorModal) ya migraron.
export function ImpactConfirmDialog({
  open,
  eyebrow,
  title,
  affectedCount,
  affectedLabel,
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Confirmar",
  danger = true,
  busy = false,
  confirming = false,
  disabled = false,
}: {
  open: boolean;
  // Revisión en vivo 2026-09-22, punto #21: el módulo o contexto corto que va
  // sobre el título (p. ej. "Seguridad · Usuarios"). Obligatorio para que
  // ningún llamador quede con el encabezado viejo.
  eyebrow: string;
  title: string;
  // `affectedCount`/`affectedLabel` componen `"${count} ${label}"`: sirven
  // para "N registros afectados". SPEC-220 §7 exige en cambio un mensaje
  // literal completo (con o sin cifra intercalada en medio de la oración,
  // nunca al principio) — `message`, si se pasa, reemplaza esa composición
  // sin tocar ningún llamador existente (ambos props vuelven opcionales).
  affectedCount?: number;
  affectedLabel?: string;
  message?: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  confirming?: boolean;
  disabled?: boolean;
}) {
  if (!open) return null;

  return (
    <Modal
      label={title}
      width={values.impact.width}
      onClose={onCancel}
      role="alertdialog"
      // Revisión en vivo 2026-09-22, puntos #4A/#15: las tres regiones del
      // `Modal` (encabezado fijo, cuerpo scrolleable, pie fijo). Los márgenes
      // que separaban título, aviso y botones se borran — no se mueven: el
      // aire entre regiones lo pone ahora el `Modal` con sus divisores.
      header={<DialogHeader eyebrow={eyebrow} title={title} />}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <PortalButton
            type="button"
            tone="secondary"
            size="small"
            disabled={busy || confirming}
            onClick={onCancel}
          >
            Cancelar
          </PortalButton>
          <PortalButton
            type="button"
            // Punto #15A: la confirmación destructiva se ve destructiva. El
            // tono lo decide el mismo `danger` que ya teñía el aviso, así que
            // un diálogo informativo (`danger={false}`) sigue en primario.
            tone={danger ? "danger" : "primary"}
            size="small"
            disabled={disabled || busy || confirming}
            aria-busy={busy || confirming}
            onClick={onConfirm}
          >
            {busy || confirming ? "Confirmando…" : confirmLabel}
          </PortalButton>
        </div>
      }
    >
      <div
        style={{
          font: `500 13px ${tokens.font.ui}`,
          color: danger ? tokens.danger.base : tokens.text.secondary,
          background: danger ? tokens.danger.dim : tokens.surface.surface,
          border: `1px solid ${danger ? tokens.danger.line : tokens.surface.line}`,
          borderRadius: values.impact.noticeRadius,
          padding: values.impact.noticePadding,
        }}
      >
        {message ?? `${affectedCount ?? 0} ${affectedLabel ?? ""}`}
      </div>
    </Modal>
  );
}
